import React from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Stack,
  ListItem,
  ListItemIcon,
  ListItemText,
  List,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  TrendingUp,
  Warning,
  CheckCircle,
  Security,
  BarChart,
  AttachMoney,
  Build,
} from "@mui/icons-material";
import { Target } from "lucide-react";
import GaugeChartMUI from "./charts/GaugeChartMUI";
import CircularProgressMUI from "./charts/CircularProgressMUI";
import InfoIcon from "@mui/icons-material/Info";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BarChartIcon from "@mui/icons-material/BarChart";
import CancelIcon from "@mui/icons-material/Cancel";
import TrueCheckCircleIcon from "@mui/icons-material/CheckCircle";
import GroupIcon from "@mui/icons-material/Group";
import FlashOnIcon from "@mui/icons-material/FlashOn";

const typographyStyles = {
  title: { fontSize: "23px", fontWeight: 500 },
  subtitle: { fontSize: "18px", fontWeight: 600 },
  body: { fontSize: "18px", fontWeight: 400 },
  caption: { fontSize: "16px", fontWeight: 400 },
};

const tooltipTexts = {
  "Overall Security Posture":
    "Represents the percentage of the application’s security resilience based on identified dynamic vulnerabilities during runtime testing. A lower value indicates stronger security hygiene, reduced exploitable weaknesses, and better overall application defense.",
  "True Positive Volume Analysis":
    "Represents the total number of confirmed security vulnerabilities identified during testing. Higher counts indicate greater security exposure requiring immediate attention.",
  "Severity Breakdown":
    "Categorizes vulnerabilities by their potential impact level. Critical and High severity issues require immediate remediation to prevent security breaches.",
  "Return on Investment":
    "Demonstrates the financial value of security testing by comparing prevention costs to potential breach costs. Higher ROI indicates effective security investment.",
  "Compliance Gap Mapping":
    "Maps identified vulnerabilities to industry standards like OWASP Top 10. Shows compliance status and areas requiring attention for regulatory requirements.",
  "Risk Rating Overview":
    "Calculates the overall risk exposure based on vulnerability severity and exploitability. Higher scores indicate elevated threat levels requiring prioritized remediation.",
  "False Positive Rate":
    "Percentage of incorrectly identified vulnerabilities. High rates indicate scanning tool inefficiency and increased manual validation effort.",
  "True Positive Rate":
    "Percentage of correctly identified vulnerabilities. Higher rates indicate effective security testing coverage and accurate threat detection capabilities.",
  "Business Impact Score":
    "Indicates how vulnerabilities can disrupt business operations or services. Lower percentages suggest minimal risk to business continuity.",
  "Technical Impact Score":
    "Measures the potential technical damage from successful exploits including system compromise, data loss, and service disruption. Higher scores indicate severe technical risks.",
  "Strategic Recommendations Summary":
    "Provides actionable recommendations based on security analysis findings. Includes immediate actions and long-term strategic improvements for enhanced security posture.",
};

// Function to get dynamic description based on business impact score
const getBusinessImpactDescription = (score) => {
  if (score === 0.0) {
    return "None";
  } else if (score >= 0.1 && score <= 3.9) {
    return "Low Impact";
  } else if (score >= 4.0 && score <= 6.9) {
    return "Medium Impact";
  } else if (score >= 7.0 && score <= 8.9) {
    return "High Impact";
  } else if (score >= 9.0 && score <= 10.0) {
    return "Critical Impact";
  } else {
    return "Unknown Impact";
  }
};

// Helper to turn a technical-impact score into a label
const getTechnicalImpactDescription = (score) => {
  if (score === 0.0) {
    return "None";
  } else if (score >= 0.1 && score <= 3.9) {
    return "Low Impact";
  } else if (score >= 4.0 && score <= 6.9) {
    return "Medium Impact";
  } else if (score >= 7.0 && score <= 8.9) {
    return "High Impact";
  } else if (score >= 9.0 && score <= 10.0) {
    return "Critical Impact";
  }
  return "Unknown Impact";
};

export default function SingleRunInsightsRecommendationsComponent({
  singleRunSecurityMetrics,
  singleRunInsightsAndRecs,
}) {
  const securityMetrics = singleRunSecurityMetrics || {};
  const insightsAndRecs = singleRunInsightsAndRecs || [];

  const totalVulns = Object.values(
    securityMetrics?.severityBreakdown || {}
  ).reduce((sum, count) => sum + (count || 0), 0);

  const severityPercentages = {
    Critical:
      totalVulns > 0
        ? (
            ((securityMetrics?.severityBreakdown?.Critical || 0) / totalVulns) *
            100
          ).toFixed(1)
        : 0,
    High:
      totalVulns > 0
        ? (
            ((securityMetrics?.severityBreakdown?.High || 0) / totalVulns) *
            100
          ).toFixed(1)
        : 0,
    Medium:
      totalVulns > 0
        ? (
            ((securityMetrics?.severityBreakdown?.Medium || 0) / totalVulns) *
            100
          ).toFixed(1)
        : 0,
    Low:
      totalVulns > 0
        ? (
            ((securityMetrics?.severityBreakdown?.Low || 0) / totalVulns) *
            100
          ).toFixed(1)
        : 0,
  };

  const highestSeverityData = Object.entries(severityPercentages).reduce(
    (max, [name, value]) => {
      const numValue = parseFloat(value);
      return numValue > max.percentage
        ? { name, percentage: numValue, displayValue: value }
        : max;
    },
    { name: "", percentage: 0, displayValue: "0" }
  );

  // Helper function to get risk rating description
  const getRiskRatingDescription = (rating) => {
    if (rating >= 0.0 && rating <= 0.0)
      return { text: "No security concerns", color: "success.main" };
    if (rating >= 0.1 && rating <= 3.9)
      return { text: "Low", color: "success.main" };
    if (rating >= 4.0 && rating <= 6.9)
      return { text: "Medium", color: "warning.main" };
    if (rating >= 7.0 && rating <= 8.9)
      return { text: "High", color: "error.main" };
    if (rating >= 9.0 && rating <= 10.0)
      return { text: "Critical", color: "error.main" };
    return { text: "Unknown risk level", color: "text.secondary" };
  };

  // Helper function to get data for a specific section dynamically
  const getSectionData = (sectionName) => {
    const section = insightsAndRecs.find((item) =>
      item.section?.toLowerCase().includes(sectionName.toLowerCase())
    );
    return section?.data || null;
  };

  // Extract section-specific data dynamically
  const overallPostureData = getSectionData("Overall Security Posture");
  const truePositiveData = getSectionData("True Positive Volume & Exposure");
  const severityBreakdownData = getSectionData("Severity Breakdown");
  const ROIData = getSectionData("Return on Investment (ROI)");
  const complianceGAPData = getSectionData("Compliance Gap Mapping");
  const riskRatingAnalysis = getSectionData("Risk Rating Overview");
  const falsePositiveAnalysis = getSectionData("False Positive Rate");
  const truePositiveAnalysis = getSectionData("True Positive Rate");
  const businessImpactAnalysis = getSectionData(
    "Business Impact Interpretation"
  );
  const technicalImpactAnalysis = getSectionData("Technical Impact Overview");
  const StrategicRecsAnalysis = getSectionData(
    "Strategic Recommendations Summary"
  );
  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        backgroundColor: "#FFFFFF",
        minHeight: "100vh",
      }}
    >
      <Stack spacing={4}>
        {/* Overall Security Posture */}
        <Paper
          elevation={1}
          sx={{
            p: 3,
            border: "1px solid",
            borderColor: "#5391F7",
            borderRadius: 2,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            mb={3}
            sx={{ position: "relative" }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Security color="primary" />
              <Typography sx={{ fontSize: "24px", fontWeight: 400 }}>
                Overall Security Posture Analysis
              </Typography>
            </Stack>

            <Tooltip
              title={tooltipTexts["Overall Security Posture"]}
              placement="top"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: 300,
                    fontSize: "14px",
                    lineHeight: 1.4,
                  },
                },
              }}
            >
              <IconButton
                size="small"
                sx={{
                  padding: "4px",
                  color: "#5391F7",
                  "&:hover": {
                    backgroundColor: "rgba(83, 145, 247, 0.1)",
                  },
                }}
                aria-label="Info"
              >
                <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
              </IconButton>
            </Tooltip>
          </Stack>

          <Grid container spacing={3} mb={3}>
            <Grid item xs={12} md={3}>
              <Box textAlign="center">
                <Typography
                  sx={{
                    fontSize: "32px",
                    fontWeight: 500,
                    color: "#ea580c",
                    mb: 1,
                  }}
                >
                  {securityMetrics.securityPosture || 0}/10
                </Typography>
                <Typography
                  color="warning.main"
                  sx={{ fontSize: "16px", fontWeight: 400 }}
                  display="block"
                >
                  {
                    getRiskRatingDescription(securityMetrics.securityPosture)
                      .text
                  }
                </Typography>
                <Typography
                  sx={{
                    fontSize: "18px",
                    fontWeight: 500,
                    mt: 1,
                  }}
                >
                  Security Posture Score
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box textAlign="center">
                <Typography
                  sx={{
                    fontSize: "32px",
                    fontWeight: 500,
                    color: "#ea580c",
                    mb: 1,
                  }}
                >
                  {securityMetrics.riskRating || 0}
                </Typography>
                <Typography
                  color="warning.main"
                  sx={{ fontSize: "16px", fontWeight: 400 }}
                  display="block"
                >
                  {
                    getRiskRatingDescription(
                      securityMetrics.riskRatingWeightedScore
                    ).text
                  }
                </Typography>
                <Typography
                  sx={{
                    fontSize: "18px",
                    fontWeight: 500,
                    mt: 1,
                  }}
                >
                  Risk Rating
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box textAlign="center">
                <Typography
                  sx={{
                    fontSize: "32px",
                    fontWeight: 500,
                    color: "#ea580c",
                    mb: 1,
                  }}
                >
                  {securityMetrics.vulnerabilitiesCount || 0}
                </Typography>
                <Typography
                  color="warning.main"
                  sx={{ fontSize: "16px", fontWeight: 400 }}
                  display="block"
                >
                  True Positives
                </Typography>
                <Typography
                  sx={{
                    fontSize: "18px",
                    fontWeight: 500,
                    mt: 1,
                  }}
                >
                  Active Vulnerabilities
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box textAlign="center">
                <Typography
                  sx={{
                    fontSize: "34px",
                    fontWeight: 500,
                    color: "#dc2626",
                    mb: 1,
                  }}
                >
                  {securityMetrics?.severityBreakdown?.Critical +
                    securityMetrics?.severityBreakdown?.High || 0}{" "}
                </Typography>
                <Typography
                  sx={{ fontSize: "16px", fontWeight: 400 }}
                  color="warning.main"
                  display="block"
                >
                  {`${
                    securityMetrics?.severityBreakdown?.Critical || 0
                  } Critical, ${
                    securityMetrics?.severityBreakdown?.High || 0
                  } High`}
                </Typography>
                <Typography sx={{ mt: 1, fontSize: "18px", fontWeight: 400 }}>
                  Critical/High Severity
                </Typography>
              </Box>
            </Grid>
          </Grid>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              bgcolor: "grey.50",
              border: "1px solid",
              borderColor: "#787878",
              borderRadius: 1,
            }}
          >
            <Typography sx={{ fontSize: "18px", fontWeight: 400, mb: 1 }}>
              <strong>Insights:</strong>
            </Typography>
            {overallPostureData?.key_insights?.length > 0 ? (
              <Stack spacing={1} sx={{ ml: 2, mb: 2 }}>
                {overallPostureData.key_insights.map((cause, index) => (
                  <Typography
                    key={index}
                    sx={{ fontSize: "16px", fontWeight: 400 }}
                    color="text.secondary"
                  >
                    {index + 1}. {cause}
                  </Typography>
                ))}
              </Stack>
            ) : (
              <Typography
                sx={{ fontSize: "16px", fontWeight: 400, mb: 2 }}
                color="text.secondary"
              >
                No insights are available
              </Typography>
            )}

            <Typography sx={{ fontSize: "18px", fontWeight: 400, mb: 1 }}>
              <strong>Root Cause:</strong>
            </Typography>
            {overallPostureData?.root_cause_analysis?.length > 0 ? (
              <Stack spacing={1} sx={{ ml: 2, mb: 2 }}>
                {overallPostureData.root_cause_analysis.map((cause, index) => (
                  <Typography
                    key={index}
                    sx={{ fontSize: "16px", fontWeight: 400 }}
                    color="text.secondary"
                  >
                    {index + 1}. {cause}
                  </Typography>
                ))}
              </Stack>
            ) : (
              <Typography
                sx={{ fontSize: "16px", fontWeight: 400, mb: 2 }}
                color="text.secondary"
              >
                No root cause analysis available
              </Typography>
            )}
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              mt: 3,
              bgcolor: "grey.50",
              border: "1px solid",
              borderColor: "#787878",
              borderRadius: 1,
              // backgroundColor: "#bfdbfe",
            }}
          >
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: 400,
                mb: 1,
                color: "#E3112F",
              }}
            >
              <strong>Action Items:</strong>
            </Typography>
            {overallPostureData?.tangible_actions?.length > 0 ? (
              <Stack spacing={1} sx={{ ml: 2 }}>
                {overallPostureData.tangible_actions.map(
                  (actionItem, index) => (
                    <Stack
                      key={index}
                      direction="row"
                      spacing={1}
                      alignItems="flex-start"
                    >
                      <Typography
                        sx={{ fontSize: "16px", fontWeight: 400 }}
                        color="text.secondary"
                      >
                        {index + 1}. {actionItem.action}
                      </Typography>
                      {/* <Chip
                        label={actionItem.responsible_role}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ ml: 1 }}
                      /> */}
                    </Stack>
                  )
                )}
              </Stack>
            ) : (
              <Typography
                sx={{ fontSize: "16px", fontWeight: 400 }}
                color="text.secondary"
              >
                No Action Items available
              </Typography>
            )}
          </Paper>
        </Paper>

        {/* True Positive Volume Analysis */}
        <Paper
          elevation={1}
          sx={{
            p: 3,
            border: "1px solid",
            borderColor: "#5391F7",
            borderRadius: 2,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            mb={3}
            sx={{ position: "relative" }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <BarChart color="primary" />
              <Typography sx={{ fontSize: "24px", fontWeight: 400 }}>
                True Positive Volume Analysis
              </Typography>
            </Stack>

            <Tooltip
              title={tooltipTexts["True Positive Volume Analysis"]}
              placement="top"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: 300,
                    fontSize: "14px",
                    lineHeight: 1.4,
                  },
                },
              }}
            >
              <IconButton
                size="small"
                sx={{
                  padding: "4px",
                  color: "#5391F7",
                  "&:hover": {
                    backgroundColor: "rgba(83, 145, 247, 0.1)",
                  },
                }}
                aria-label="Info"
              >
                <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
              </IconButton>
            </Tooltip>
          </Stack>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Typography sx={{ fontSize: "18px", fontWeight: 400 }}>
                  Exposure Level
                </Typography>
                <Typography
                  sx={{ fontSize: "16px", fontWeight: 400, color: "#dc2626" }}
                >
                  {securityMetrics.vulnerabilitiesCount || 0} true positive
                  vulnerabilities
                </Typography>
              </Box>
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}
              >
                <Typography sx={{ fontSize: "18px", fontWeight: 400 }}>
                  Identified OWASP Categories:
                </Typography>
                {(() => {
                  // Group and count OWASP categories
                  const owaspCategoryCounts =
                    securityMetrics.owasp_metrics.reduce((acc, item) => {
                      const category = item.owasp_category;
                      if (acc[category]) {
                        acc[category].count += 1;
                        acc[category].vulnerabilities.push(
                          item["vulnerability name"]
                        );
                      } else {
                        acc[category] = {
                          count: 1,
                          vulnerabilities: [item["vulnerability name"]],
                        };
                      }
                      return acc;
                    }, {});

                  // Convert to array and sort by count (descending)
                  return Object.entries(owaspCategoryCounts)
                    .sort(([, a], [, b]) => b.count - a.count)
                    .map(([category, data], index) => (
                      <Box key={index}>
                        <Typography
                          sx={{
                            fontSize: "15px",
                            fontWeight: 400,
                            color: "#CF5006",
                          }}
                        >
                          • {category}: {data.count} finding
                          {data.count > 1 ? "s" : ""}
                        </Typography>
                      </Box>
                    ));
                })()}
              </Box>
            </Grid>
          </Grid>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mt: 4,
              bgcolor: "grey.50",
              border: "1px solid",
              borderColor: "#787878",
              borderRadius: 1,
            }}
          >
            <Typography sx={{ fontSize: "18px", fontWeight: 400, mb: 1 }}>
              <strong>Insights:</strong>
            </Typography>
            {truePositiveData?.key_insights?.length > 0 ? (
              <Stack spacing={1} sx={{ ml: 2, mb: 2 }}>
                {truePositiveData.key_insights.map((cause, index) => (
                  <Typography
                    key={index}
                    sx={{ fontSize: "16px", fontWeight: 400 }}
                    color="text.secondary"
                  >
                    {index + 1}. {cause}
                  </Typography>
                ))}
              </Stack>
            ) : (
              <Typography
                sx={{ fontSize: "16px", fontWeight: 400, mb: 2 }}
                color="text.secondary"
              >
                No insights are available
              </Typography>
            )}

            <Typography sx={{ fontSize: "18px", fontWeight: 400, mb: 1 }}>
              <strong>Root Cause:</strong>
            </Typography>
            {truePositiveData?.root_cause_analysis?.length > 0 ? (
              <Stack spacing={1} sx={{ ml: 2, mb: 2 }}>
                {truePositiveData.root_cause_analysis.map((cause, index) => (
                  <Typography
                    key={index}
                    sx={{ fontSize: "16px", fontWeight: 400 }}
                    color="text.secondary"
                  >
                    {index + 1}. {cause}
                  </Typography>
                ))}
              </Stack>
            ) : (
              <Typography
                sx={{ fontSize: "16px", fontWeight: 400, mb: 2 }}
                color="text.secondary"
              >
                No root cause analysis available
              </Typography>
            )}
          </Paper>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mt: 4,
              bgcolor: "grey.50",
              border: "1px solid",
              borderColor: "#787878",
              borderRadius: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: 400,
                mb: 1,
                color: "#E3112F",
              }}
            >
              <strong>Action Items:</strong>
            </Typography>
            {truePositiveData?.tangible_actions?.length > 0 ? (
              <Stack spacing={1} sx={{ ml: 2 }}>
                {truePositiveData.tangible_actions.map((actionItem, index) => (
                  <Stack
                    key={index}
                    direction="row"
                    spacing={1}
                    alignItems="flex-start"
                  >
                    <Typography
                      sx={{ fontSize: "16px", fontWeight: 400 }}
                      color="text.secondary"
                    >
                      {index + 1}. {actionItem.action}
                    </Typography>
                    {/* <Chip
                      label={actionItem.responsible_role}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ ml: 1 }}
                    /> */}
                  </Stack>
                ))}
              </Stack>
            ) : (
              <Typography
                sx={{ fontSize: "16px", fontWeight: 400 }}
                color="text.secondary"
              >
                No Action Items available
              </Typography>
            )}
          </Paper>
        </Paper>

        {/* Severity Breakdown Analysis */}
        <Paper
          elevation={1}
          sx={{
            p: 3,
            border: "1px solid",
            borderColor: "#5391F7",
            borderRadius: 2,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            mb={3}
            sx={{ position: "relative" }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Warning color="warning" />
              <Typography sx={{ fontSize: "24px", fontWeight: 400 }}>
                Severity Breakdown
              </Typography>
            </Stack>

            <Tooltip
              title={tooltipTexts["Severity Breakdown"]}
              placement="top"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: 300,
                    fontSize: "14px",
                    lineHeight: 1.4,
                  },
                },
              }}
            >
              <IconButton
                size="small"
                sx={{
                  padding: "4px",
                  color: "#5391F7",
                  "&:hover": {
                    backgroundColor: "rgba(83, 145, 247, 0.1)",
                  },
                }}
                aria-label="Info"
              >
                <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
              </IconButton>
            </Tooltip>
          </Stack>
          <Grid container spacing={3} mb={3}>
            <Grid item xs={12}>
              <Box sx={{ p: 1, display: "flex" }}>
                <Typography sx={{ fontSize: "16px", fontWeight: 400 }}>
                  <strong>Dominant Severity:</strong>{" "}
                  <Box
                    component="span"
                    sx={{ color: "error.main", fontWeight: 500 }}
                  >
                    {highestSeverityData.name} (
                    {highestSeverityData.percentage.toFixed(1)}%)
                  </Box>
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ fontSize: "18px", fontWeight: 400, mb: 2 }}>
                Vulnerability Distribution Analysis
              </Typography>
              <Stack spacing={1.5}>
                {[
                  {
                    label: `Critical (${severityPercentages.Critical}%)`,
                    value: `${
                      securityMetrics.severityBreakdown?.Critical || 0
                    } vulnerabilities`,
                    color: "#dc2626",
                    percentage: severityPercentages.Critical,
                  },
                  {
                    label: `High (${severityPercentages.High}%)`,
                    value: `${
                      securityMetrics.severityBreakdown?.High || 0
                    } vulnerabilities`,
                    color: "#ea580c",
                    percentage: severityPercentages.High,
                  },
                  {
                    label: `Medium (${severityPercentages.Medium}%)`,
                    value: `${
                      securityMetrics.severityBreakdown?.Medium || 0
                    } vulnerabilities`,
                    color: "#d97706",
                    percentage: severityPercentages.Medium,
                  },
                  {
                    label: `Low (${severityPercentages.Low}%)`,
                    value: `${
                      securityMetrics.severityBreakdown?.Low || 0
                    } vulnerabilities`,
                    color: "#16a34a",
                    percentage: severityPercentages.Low,
                  },
                ].map((item, index) => (
                  <Paper
                    key={index}
                    elevation={0}
                    sx={{
                      p: 1.5,
                      bgcolor: "grey.50",
                      border: "2px solid",
                      borderColor: "grey.300",
                      borderRadius: 1,
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: "50%",
                            bgcolor: item.color,
                          }}
                        />
                        <Typography sx={{ fontSize: "16px", fontWeight: 400 }}>
                          {item.label}
                        </Typography>
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography
                          sx={{
                            fontSize: "16px",
                            fontWeight: 500,
                            color: item.color,
                          }}
                        >
                          {item.value}
                        </Typography>
                        <CircularProgressMUI
                          percentage={item.percentage}
                          color={item.color}
                          size={30}
                          showPercentage={false}
                        />
                      </Stack>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Grid>
          </Grid>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mt: 4,
              bgcolor: "grey.50",
              border: "1px solid",
              borderColor: "#787878",
              borderRadius: 1,
            }}
          >
            <Typography sx={{ fontSize: "18px", fontWeight: 400, mb: 1 }}>
              <strong>Insights:</strong>
            </Typography>
            {severityBreakdownData?.key_insights?.length > 0 ? (
              <Stack spacing={1} sx={{ ml: 2, mb: 2 }}>
                {severityBreakdownData.key_insights.map((cause, index) => (
                  <Typography
                    key={index}
                    sx={{ fontSize: "16px", fontWeight: 400 }}
                    color="text.secondary"
                  >
                    {index + 1}. {cause}
                  </Typography>
                ))}
              </Stack>
            ) : (
              <Typography
                sx={{ fontSize: "16px", fontWeight: 400, mb: 2 }}
                color="text.secondary"
              >
                No insights are available
              </Typography>
            )}

            <Typography sx={{ fontSize: "18px", fontWeight: 400, mb: 1 }}>
              <strong>Root Cause:</strong>
            </Typography>
            {severityBreakdownData?.root_cause_analysis?.length > 0 ? (
              <Stack spacing={1} sx={{ ml: 2, mb: 2 }}>
                {severityBreakdownData.root_cause_analysis.map(
                  (cause, index) => (
                    <Typography
                      key={index}
                      sx={{ fontSize: "16px", fontWeight: 400 }}
                      color="text.secondary"
                    >
                      {index + 1}. {cause}
                    </Typography>
                  )
                )}
              </Stack>
            ) : (
              <Typography
                sx={{ fontSize: "16px", fontWeight: 400, mb: 2 }}
                color="text.secondary"
              >
                No root cause analysis available
              </Typography>
            )}
          </Paper>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mt: 4,
              bgcolor: "grey.50",
              border: "1px solid",
              borderColor: "#787878",
              borderRadius: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: 400,
                mb: 1,
                color: "#E3112F",
              }}
            >
              <strong>Action Items:</strong>
            </Typography>
            {severityBreakdownData?.tangible_actions?.length > 0 ? (
              <Stack spacing={1} sx={{ ml: 2 }}>
                {severityBreakdownData.tangible_actions.map(
                  (actionItem, index) => (
                    <Stack
                      key={index}
                      direction="row"
                      spacing={1}
                      alignItems="flex-start"
                    >
                      <Typography
                        sx={{ fontSize: "16px", fontWeight: 400 }}
                        color="text.secondary"
                      >
                        {index + 1}. {actionItem.action}
                      </Typography>
                    </Stack>
                  )
                )}
              </Stack>
            ) : (
              <Typography
                sx={{ fontSize: "16px", fontWeight: 400 }}
                color="text.secondary"
              >
                No Action Items available
              </Typography>
            )}
          </Paper>
        </Paper>

        {/* Return on Investment Analysis */}
        <Paper
          elevation={1}
          sx={{
            p: 3,
            border: "1px solid",
            borderColor: "#5391F7",
            borderRadius: 2,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            mb={3}
            sx={{ position: "relative" }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <AttachMoney color="success" />
              <Typography sx={{ fontSize: "24px", fontWeight: 400 }}>
                Return on Investment Analysis
              </Typography>
            </Stack>

            <Tooltip
              title={tooltipTexts["Return on Investment Analysis"]}
              placement="top"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: 300,
                    fontSize: "14px",
                    lineHeight: 1.4,
                  },
                },
              }}
            >
              <IconButton
                size="small"
                sx={{
                  padding: "4px",
                  color: "#5391F7",
                  "&:hover": {
                    backgroundColor: "rgba(83, 145, 247, 0.1)",
                  },
                }}
                aria-label="Info"
              >
                <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
              </IconButton>
            </Tooltip>
          </Stack>
          <Grid container spacing={3} mb={2}>
            <Grid item xs={12}>
              <Box textAlign="center">
                <Typography
                  sx={{ fontSize: "42px", fontWeight: 600 }}
                  color="#39b549"
                  mb={1}
                >
                  {`${securityMetrics.ROI || "0"}%`}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: 400,
                    mb: 1,
                  }}
                >
                  ROI Multiplier
                </Typography>

                {/* <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 400,
                    color: "#39b549",
                    mt: 0.5,
                  }}
                  display="block"
                >
                  Excellent performance
                </Typography> */}
              </Box>
            </Grid>
          </Grid>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mt: 4,
              bgcolor: "grey.50",
              border: "1px solid",
              borderColor: "#787878",
              borderRadius: 1,
            }}
          >
            <Typography sx={{ fontSize: "18px", fontWeight: 400, mb: 1 }}>
              <strong>Insights:</strong>
            </Typography>
            {ROIData?.key_insights?.length > 0 ? (
              <Stack spacing={1} sx={{ ml: 2, mb: 2 }}>
                {ROIData.key_insights.map((cause, index) => (
                  <Typography
                    key={index}
                    sx={{ fontSize: "16px", fontWeight: 400 }}
                    color="text.secondary"
                  >
                    {index + 1}. {cause}
                  </Typography>
                ))}
              </Stack>
            ) : (
              <Typography
                sx={{ fontSize: "16px", fontWeight: 400, mb: 2 }}
                color="text.secondary"
              >
                No insights available
              </Typography>
            )}

            <Typography sx={{ fontSize: "18px", fontWeight: 400, mb: 1 }}>
              <strong>Root Cause:</strong>
            </Typography>
            {ROIData?.root_cause_analysis?.length > 0 ? (
              <Stack spacing={1} sx={{ ml: 2, mb: 2 }}>
                {ROIData.root_cause_analysis.map((cause, index) => (
                  <Typography
                    key={index}
                    sx={{ fontSize: "16px", fontWeight: 400 }}
                    color="text.secondary"
                  >
                    {index + 1}. {cause}
                  </Typography>
                ))}
              </Stack>
            ) : (
              <Typography
                sx={{ fontSize: "16px", fontWeight: 400, mb: 2 }}
                color="text.secondary"
              >
                No root cause analysis available
              </Typography>
            )}
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              mt: 4,
              bgcolor: "grey.50",
              border: "1px solid",
              borderColor: "#787878",
              borderRadius: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: 400,
                mb: 1,
                color: "#E3112F",
              }}
            >
              <strong>Action Items:</strong>
            </Typography>
            {ROIData?.tangible_actions?.length > 0 ? (
              <Stack spacing={1} sx={{ ml: 2 }}>
                {ROIData.tangible_actions.map((actionItem, index) => (
                  <Stack
                    key={index}
                    direction="row"
                    spacing={1}
                    alignItems="flex-start"
                  >
                    <Typography
                      sx={{ fontSize: "16px", fontWeight: 400 }}
                      color="text.secondary"
                    >
                      {index + 1}. {actionItem.action}
                    </Typography>
                    {/* <Chip
                      label={actionItem.responsible_role}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ ml: 1 }}
                    /> */}
                  </Stack>
                ))}
              </Stack>
            ) : (
              <Typography
                sx={{ fontSize: "16px", fontWeight: 400 }}
                color="text.secondary"
              >
                No Action Items available
              </Typography>
            )}
          </Paper>
        </Paper>

        {/* Compliance GAP */}
        <Paper
          elevation={1}
          sx={{
            p: 3,
            border: "1px solid",
            borderColor: "#5391F7",
            borderRadius: 2,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            mb={3}
            sx={{ position: "relative" }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <CheckCircleIcon
                sx={{
                  color: "#16a34a", // green-600 equivalent
                  fontSize: "1.25rem", // h-5 w-5 equivalent (20px)
                  marginRight: "0.5rem", // mr-2 equivalent
                }}
              />
              <Typography sx={{ fontSize: "24px", fontWeight: 400 }}>
                Compliance Gap Mapping
              </Typography>
            </Stack>

            <Tooltip
              title={tooltipTexts["Compliance Gap Mapping"]}
              placement="top"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: 300,
                    fontSize: "14px",
                    lineHeight: 1.4,
                  },
                },
              }}
            >
              <IconButton
                size="small"
                sx={{
                  padding: "4px",
                  color: "#5391F7",
                  "&:hover": {
                    backgroundColor: "rgba(83, 145, 247, 0.1)",
                  },
                }}
                aria-label="Info"
              >
                <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
              </IconButton>
            </Tooltip>
          </Stack>
          <Grid
            item
            xs={12}
            md={6}
            sx={{ display: { xs: "none", md: "block" } }}
          >
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 2 }}
            >
              <Typography sx={{ fontSize: "18px", fontWeight: 400 }}>
                OWASP Top 10 Mapping:
              </Typography>
              {(() => {
                // Group and count OWASP categories
                const owaspCategoryCounts =
                  securityMetrics.owasp_metrics.reduce((acc, item) => {
                    const category = item.owasp_category;
                    if (acc[category]) {
                      acc[category].count += 1;
                      acc[category].vulnerabilities.push(
                        item["vulnerability name"]
                      );
                    } else {
                      acc[category] = {
                        count: 1,
                        vulnerabilities: [item["vulnerability name"]],
                      };
                    }
                    return acc;
                  }, {});

                // Convert to array and sort by count (descending)
                return Object.entries(owaspCategoryCounts)
                  .sort(([, a], [, b]) => b.count - a.count)
                  .map(([category, data], index) => (
                    <Stack
                      key={index}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{
                        flexWrap: { xs: "wrap", sm: "nowrap" },
                        gap: { xs: 1, sm: 2 },
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: { xs: "14px", sm: "15px" },
                          fontWeight: 400,
                          color: "#CF5006",
                          flex: 1,
                          minWidth: { xs: "100%", sm: "auto" },
                          wordBreak: "break-word",
                        }}
                      >
                        • {category}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: { xs: "13px", sm: "14px" },
                          fontWeight: 500,
                          color: "#CF5006",
                          // bgcolor: "#f0f7ff",
                          px: 1,
                          py: 0.25,
                          borderRadius: 1,
                          minWidth: "fit-content",
                        }}
                      >
                        {data.count}
                      </Typography>
                    </Stack>
                  ));
              })()}
            </Box>
          </Grid>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              mt: 4,
              bgcolor: "grey.50",
              border: "1px solid",
              borderColor: "#787878",
              borderRadius: 1,
            }}
          >
            <Typography sx={{ fontSize: "18px", fontWeight: 400, mb: 1 }}>
              <strong>Insights:</strong>
            </Typography>
            {complianceGAPData?.key_insights?.length > 0 ? (
              <Stack spacing={1} sx={{ ml: 2, mb: 2 }}>
                {complianceGAPData.key_insights.map((cause, index) => (
                  <Typography
                    key={index}
                    sx={{ fontSize: "16px", fontWeight: 400 }}
                    color="text.secondary"
                  >
                    {index + 1}. {cause}
                  </Typography>
                ))}
              </Stack>
            ) : (
              <Typography
                sx={{ fontSize: "16px", fontWeight: 400, mb: 2 }}
                color="text.secondary"
              >
                No insights available
              </Typography>
            )}

            <Typography sx={{ fontSize: "18px", fontWeight: 400, mb: 1 }}>
              <strong>Root Cause:</strong>
            </Typography>
            {complianceGAPData?.root_cause_analysis?.length > 0 ? (
              <Stack spacing={1} sx={{ ml: 2, mb: 2 }}>
                {complianceGAPData.root_cause_analysis.map((cause, index) => (
                  <Typography
                    key={index}
                    sx={{ fontSize: "16px", fontWeight: 400 }}
                    color="text.secondary"
                  >
                    {index + 1}. {cause}
                  </Typography>
                ))}
              </Stack>
            ) : (
              <Typography
                sx={{ fontSize: "16px", fontWeight: 400, mb: 2 }}
                color="text.secondary"
              >
                No root cause analysis available
              </Typography>
            )}
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              mt: 4,
              bgcolor: "grey.50",
              border: "1px solid",
              borderColor: "#787878",
              borderRadius: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: 400,
                mb: 1,
                color: "#E3112F",
              }}
            >
              <strong>Action Items:</strong>
            </Typography>
            {complianceGAPData?.tangible_actions?.length > 0 ? (
              <Stack spacing={1} sx={{ ml: 2 }}>
                {complianceGAPData.tangible_actions.map((actionItem, index) => (
                  <Stack
                    key={index}
                    direction="row"
                    spacing={1}
                    alignItems="flex-start"
                  >
                    <Typography
                      sx={{ fontSize: "16px", fontWeight: 400 }}
                      color="text.secondary"
                    >
                      {index + 1}. {actionItem.action}
                    </Typography>
                    {/* <Chip
                      label={actionItem.responsible_role}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ ml: 1 }}
                    /> */}
                  </Stack>
                ))}
              </Stack>
            ) : (
              <Typography
                sx={{ fontSize: "16px", fontWeight: 400 }}
                color="text.secondary"
              >
                No Action Items available
              </Typography>
            )}
          </Paper>
        </Paper>

        {/* Risk Rating OverView */}
        <Paper
          elevation={1}
          sx={{
            p: 3,
            border: "1px solid",
            borderColor: "#5391F7",
            borderRadius: 2,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            mb={3}
            sx={{ position: "relative" }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <BarChartIcon
                sx={{
                  color: "#dc2626",
                  fontSize: "1.25rem",
                  marginRight: "0.5rem",
                }}
              />
              <Typography sx={{ fontSize: "24px", fontWeight: 400 }}>
                Risk Rating Overview
              </Typography>
            </Stack>

            <Tooltip
              title={tooltipTexts["Risk Rating Overview"]}
              placement="top"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: 300,
                    fontSize: "14px",
                    lineHeight: 1.4,
                  },
                },
              }}
            >
              <IconButton
                size="small"
                sx={{
                  padding: "4px",
                  color: "#5391F7",
                  "&:hover": {
                    backgroundColor: "rgba(83, 145, 247, 0.1)",
                  },
                }}
                aria-label="Info"
              >
                <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
              </IconButton>
            </Tooltip>
          </Stack>

          <Grid container spacing={3} mb={3}>
            <Grid item xs={12} md={6}>
              <Box textAlign="center">
                <Typography
                  sx={{
                    fontSize: "32px",
                    fontWeight: 500,
                    color: "#ea580c",
                    mb: 1,
                  }}
                >
                  {securityMetrics.riskRating || 0}
                </Typography>
                <Typography
                  color="warning.main"
                  sx={{ fontSize: "16px", fontWeight: 400 }}
                  display="block"
                >
                  {
                    getRiskRatingDescription(
                      securityMetrics.riskRatingWeightedScore
                    ).text
                  }
                </Typography>
                <Typography
                  sx={{
                    fontSize: "18px",
                    fontWeight: 500,
                    mt: 1,
                  }}
                >
                  Risk Rating
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box textAlign="center">
                <Typography
                  sx={{
                    fontSize: "32px",
                    fontWeight: 500,
                    color: "#ea580c",
                    mb: 1,
                  }}
                >
                  {securityMetrics.riskRatingWeightedScore || 0}
                </Typography>
                <Typography
                  color="warning.main"
                  sx={{ fontSize: "16px", fontWeight: 400 }}
                  display="block"
                >
                  {
                    getRiskRatingDescription(
                      securityMetrics.riskRatingWeightedScore
                    ).text
                  }
                </Typography>
                <Typography
                  sx={{
                    fontSize: "18px",
                    fontWeight: 500,
                    mt: 1,
                  }}
                >
                  Weighted Score
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              mt: 4,
              bgcolor: "grey.50",
              border: "1px solid",
              borderColor: "#787878",
              borderRadius: 1,
            }}
          >
            <Typography sx={{ fontSize: "18px", fontWeight: 400, mb: 1 }}>
              <strong>Insights:</strong>
            </Typography>
            {riskRatingAnalysis?.key_insights?.length > 0 ? (
              <Stack spacing={1} sx={{ ml: 2, mb: 2 }}>
                {riskRatingAnalysis.key_insights.map((cause, index) => (
                  <Typography
                    key={index}
                    sx={{ fontSize: "16px", fontWeight: 400 }}
                    color="text.secondary"
                  >
                    {index + 1}. {cause}
                  </Typography>
                ))}
              </Stack>
            ) : (
              <Typography
                sx={{ fontSize: "16px", fontWeight: 400, mb: 2 }}
                color="text.secondary"
              >
                No insights available
              </Typography>
            )}

            <Typography sx={{ fontSize: "18px", fontWeight: 400, mb: 1 }}>
              <strong>Root Cause:</strong>
            </Typography>
            {riskRatingAnalysis?.root_cause_analysis?.length > 0 ? (
              <Stack spacing={1} sx={{ ml: 2, mb: 2 }}>
                {riskRatingAnalysis.root_cause_analysis.map((cause, index) => (
                  <Typography
                    key={index}
                    sx={{ fontSize: "16px", fontWeight: 400 }}
                    color="text.secondary"
                  >
                    {index + 1}. {cause}
                  </Typography>
                ))}
              </Stack>
            ) : (
              <Typography
                sx={{ fontSize: "16px", fontWeight: 400, mb: 2 }}
                color="text.secondary"
              >
                No root cause analysis available
              </Typography>
            )}
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              mt: 4,
              bgcolor: "grey.50",
              border: "1px solid",
              borderColor: "#787878",
              borderRadius: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: 400,
                mb: 1,
                color: "#E3112F",
              }}
            >
              <strong>Action Items:</strong>
            </Typography>
            {riskRatingAnalysis?.tangible_actions?.length > 0 ? (
              <Stack spacing={1} sx={{ ml: 2 }}>
                {riskRatingAnalysis.tangible_actions.map(
                  (actionItem, index) => (
                    <Stack
                      key={index}
                      direction="row"
                      spacing={1}
                      alignItems="flex-start"
                    >
                      <Typography
                        sx={{ fontSize: "16px", fontWeight: 400 }}
                        color="text.secondary"
                      >
                        {index + 1}. {actionItem.action}
                      </Typography>
                      {/* <Chip
                      label={actionItem.responsible_role}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ ml: 1 }}
                    /> */}
                    </Stack>
                  )
                )}
              </Stack>
            ) : (
              <Typography
                sx={{ fontSize: "16px", fontWeight: 400 }}
                color="text.secondary"
              >
                No Action Items available
              </Typography>
            )}
          </Paper>
        </Paper>

        {/* False Positive Rate */}
        <Paper
          elevation={1}
          sx={{
            p: 3,
            border: "1px solid",
            borderColor: "#5391F7",
            borderRadius: 2,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            mb={3}
            sx={{ position: "relative" }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <CancelIcon
                sx={{
                  color: "#dc2626",
                  fontSize: "1.25rem",
                  marginRight: "0.5rem",
                }}
              />
              <Typography sx={{ fontSize: "24px", fontWeight: 400 }}>
                False Positive Rate
              </Typography>
            </Stack>

            <Tooltip
              title={tooltipTexts["False Positive Rate"]}
              placement="top"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: 300,
                    fontSize: "14px",
                    lineHeight: 1.4,
                  },
                },
              }}
            >
              <IconButton
                size="small"
                sx={{
                  padding: "4px",
                  color: "#5391F7",
                  "&:hover": {
                    backgroundColor: "rgba(83, 145, 247, 0.1)",
                  },
                }}
                aria-label="Info"
              >
                <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
              </IconButton>
            </Tooltip>
          </Stack>

          <Grid
            item
            xs={12}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{ fontSize: "42px", fontWeight: 600, color: "#dc2626" }}
            >
              {securityMetrics.falsePositiveRate}%
            </Typography>
            <Typography sx={{ fontSize: "18px", fontWeight: 500 }}>
              False Positive Rate
            </Typography>
            {/* <Typography sx={{ fontSize: "16px", fontWeight: 400 }}>
              High
            </Typography> */}
          </Grid>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              mt: 4,
              bgcolor: "grey.50",
              border: "1px solid",
              borderColor: "#787878",
              borderRadius: 1,
            }}
          >
            <Typography sx={{ fontSize: "18px", fontWeight: 400, mb: 1 }}>
              <strong>Insights:</strong>
            </Typography>
            {falsePositiveAnalysis?.key_insights?.length > 0 ? (
              <Stack spacing={1} sx={{ ml: 2, mb: 2 }}>
                {falsePositiveAnalysis.key_insights.map((cause, index) => (
                  <Typography
                    key={index}
                    sx={{ fontSize: "16px", fontWeight: 400 }}
                    color="text.secondary"
                  >
                    {index + 1}. {cause}
                  </Typography>
                ))}
              </Stack>
            ) : (
              <Typography
                sx={{ fontSize: "16px", fontWeight: 400, mb: 2 }}
                color="text.secondary"
              >
                No insights available
              </Typography>
            )}

            <Typography sx={{ fontSize: "18px", fontWeight: 400, mb: 1 }}>
              <strong>Root Cause:</strong>
            </Typography>
            {falsePositiveAnalysis?.root_cause_analysis?.length > 0 ? (
              <Stack spacing={1} sx={{ ml: 2, mb: 2 }}>
                {falsePositiveAnalysis.root_cause_analysis.map(
                  (cause, index) => (
                    <Typography
                      key={index}
                      sx={{ fontSize: "16px", fontWeight: 400 }}
                      color="text.secondary"
                    >
                      {index + 1}. {cause}
                    </Typography>
                  )
                )}
              </Stack>
            ) : (
              <Typography
                sx={{ fontSize: "16px", fontWeight: 400, mb: 2 }}
                color="text.secondary"
              >
                No root cause analysis available
              </Typography>
            )}
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              mt: 4,
              bgcolor: "grey.50",
              border: "1px solid",
              borderColor: "#787878",
              borderRadius: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: 400,
                mb: 1,
                color: "#E3112F",
              }}
            >
              <strong>Action Items:</strong>
            </Typography>
            {falsePositiveAnalysis?.tangible_actions?.length > 0 ? (
              <Stack spacing={1} sx={{ ml: 2 }}>
                {falsePositiveAnalysis.tangible_actions.map(
                  (actionItem, index) => (
                    <Stack
                      key={index}
                      direction="row"
                      spacing={1}
                      alignItems="flex-start"
                    >
                      <Typography
                        sx={{ fontSize: "16px", fontWeight: 400 }}
                        color="text.secondary"
                      >
                        {index + 1}. {actionItem.action}
                      </Typography>
                      {/* <Chip
                        label={actionItem.responsible_role}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ ml: 1 }}
                      /> */}
                    </Stack>
                  )
                )}
              </Stack>
            ) : (
              <Typography
                sx={{ fontSize: "16px", fontWeight: 400 }}
                color="text.secondary"
              >
                No Action Items available
              </Typography>
            )}
          </Paper>
        </Paper>

        {/* True Positive Rate */}
        <Paper
          elevation={1}
          sx={{
            p: 3,
            border: "1px solid",
            borderColor: "#5391F7",
            borderRadius: 2,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            mb={3}
            sx={{ position: "relative" }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <TrueCheckCircleIcon
                sx={{
                  color: "#2563eb", // blue-600 equivalent
                  fontSize: "1.25rem", // h-5 w-5 equivalent (20px)
                  marginRight: "0.5rem", // mr-2 equivalent
                }}
              />
              <Typography sx={{ fontSize: "24px", fontWeight: 400 }}>
                True Positive Rate
              </Typography>
            </Stack>

            <Tooltip
              title={tooltipTexts["True Positive Rate"]}
              placement="top"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: 300,
                    fontSize: "14px",
                    lineHeight: 1.4,
                  },
                },
              }}
            >
              <IconButton
                size="small"
                sx={{
                  padding: "4px",
                  color: "#5391F7",
                  "&:hover": {
                    backgroundColor: "rgba(83, 145, 247, 0.1)",
                  },
                }}
                aria-label="Info"
              >
                <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
              </IconButton>
            </Tooltip>
          </Stack>

          <Grid
            item
            xs={12}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{ fontSize: "42px", fontWeight: 600, color: "#16a34a" }}
            >
              {securityMetrics.truePositiveRate}%
            </Typography>
            <Typography sx={{ fontSize: "18px", fontWeight: 500 }}>
              True Positive Rate
            </Typography>
            {/* <Typography sx={{ fontSize: "16px", fontWeight: 400 }}>
              Moderately Efficient Detection
            </Typography> */}
          </Grid>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              mt: 4,
              bgcolor: "grey.50",
              border: "1px solid",
              borderColor: "#787878",
              borderRadius: 1,
            }}
          >
            <Typography sx={{ fontSize: "18px", fontWeight: 400, mb: 1 }}>
              <strong>Insights:</strong>
            </Typography>
            {truePositiveAnalysis?.key_insights?.length > 0 ? (
              <Stack spacing={1} sx={{ ml: 2, mb: 2 }}>
                {truePositiveAnalysis.key_insights.map((cause, index) => (
                  <Typography
                    key={index}
                    sx={{ fontSize: "16px", fontWeight: 400 }}
                    color="text.secondary"
                  >
                    {index + 1}. {cause}
                  </Typography>
                ))}
              </Stack>
            ) : (
              <Typography
                sx={{ fontSize: "16px", fontWeight: 400, mb: 2 }}
                color="text.secondary"
              >
                No insights available
              </Typography>
            )}

            <Typography sx={{ fontSize: "18px", fontWeight: 400, mb: 1 }}>
              <strong>Root Cause:</strong>
            </Typography>
            {truePositiveAnalysis?.root_cause_analysis?.length > 0 ? (
              <Stack spacing={1} sx={{ ml: 2, mb: 2 }}>
                {truePositiveAnalysis.root_cause_analysis.map(
                  (cause, index) => (
                    <Typography
                      key={index}
                      sx={{ fontSize: "16px", fontWeight: 400 }}
                      color="text.secondary"
                    >
                      {index + 1}. {cause}
                    </Typography>
                  )
                )}
              </Stack>
            ) : (
              <Typography
                sx={{ fontSize: "16px", fontWeight: 400, mb: 2 }}
                color="text.secondary"
              >
                No root cause analysis available
              </Typography>
            )}
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              mt: 4,
              bgcolor: "grey.50",
              border: "1px solid",
              borderColor: "#787878",
              borderRadius: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: 400,
                mb: 1,
                color: "#E3112F",
              }}
            >
              <strong>Action Items:</strong>
            </Typography>
            {truePositiveAnalysis?.tangible_actions?.length > 0 ? (
              <Stack spacing={1} sx={{ ml: 2 }}>
                {truePositiveAnalysis.tangible_actions.map(
                  (actionItem, index) => (
                    <Stack
                      key={index}
                      direction="row"
                      spacing={1}
                      alignItems="flex-start"
                    >
                      <Typography
                        sx={{ fontSize: "16px", fontWeight: 400 }}
                        color="text.secondary"
                      >
                        {index + 1}. {actionItem.action}
                      </Typography>
                      {/* <Chip
                        label={actionItem.responsible_role}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ ml: 1 }}
                      /> */}
                    </Stack>
                  )
                )}
              </Stack>
            ) : (
              <Typography
                sx={{ fontSize: "16px", fontWeight: 400 }}
                color="text.secondary"
              >
                No Action Items available
              </Typography>
            )}
          </Paper>
        </Paper>

        {/* Business Impact  Rate */}
        <Paper
          elevation={1}
          sx={{
            p: 3,
            border: "1px solid",
            borderColor: "#5391F7",
            borderRadius: 2,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            mb={3}
            sx={{ position: "relative" }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <GroupIcon
                sx={{
                  color: "#ea580c", // orange-600 equivalent
                  fontSize: "1.5rem", // h-5 w-5 equivalent (20px)
                  marginRight: "0.5rem", // mr-2 equivalent
                }}
              />
              <Typography sx={{ fontSize: "24px", fontWeight: 400 }}>
                Business Impact Interpretation
              </Typography>
            </Stack>

            <Tooltip
              title={tooltipTexts["Business Impact Score"]}
              placement="top"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: 300,
                    fontSize: "14px",
                    lineHeight: 1.4,
                  },
                },
              }}
            >
              <IconButton
                size="small"
                sx={{
                  padding: "4px",
                  color: "#5391F7",
                  "&:hover": {
                    backgroundColor: "rgba(83, 145, 247, 0.1)",
                  },
                }}
                aria-label="Info"
              >
                <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
              </IconButton>
            </Tooltip>
          </Stack>

          <Grid
            item
            xs={12}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <GaugeChartMUI
              value={securityMetrics.businessImpact}
              max={10}
              color="#ea580c"
              size={150}
            />
            <Typography sx={{ fontSize: "18px", fontWeight: 500 }}>
              {getBusinessImpactDescription(
                singleRunSecurityMetrics.businessImpact
              )}
            </Typography>
            <Typography sx={{ fontSize: "18px", fontWeight: 500, mt: 1 }}>
              Business Impact Score
            </Typography>
          </Grid>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mt: 4,
              bgcolor: "grey.50",
              border: "1px solid",
              borderColor: "#787878",
              borderRadius: 1,
            }}
          >
            <Typography sx={{ fontSize: "18px", fontWeight: 400, mb: 1 }}>
              <strong>Insights:</strong>
            </Typography>
            {businessImpactAnalysis?.key_insights?.length > 0 ? (
              <Stack spacing={1} sx={{ ml: 2, mb: 2 }}>
                {businessImpactAnalysis.key_insights.map((cause, index) => (
                  <Typography
                    key={index}
                    sx={{ fontSize: "16px", fontWeight: 400 }}
                    color="text.secondary"
                  >
                    {index + 1}. {cause}
                  </Typography>
                ))}
              </Stack>
            ) : (
              <Typography
                sx={{ fontSize: "16px", fontWeight: 400, mb: 2 }}
                color="text.secondary"
              >
                No insights available
              </Typography>
            )}

            <Typography sx={{ fontSize: "18px", fontWeight: 400, mb: 1 }}>
              <strong>Root Cause:</strong>
            </Typography>
            {businessImpactAnalysis?.root_cause_analysis?.length > 0 ? (
              <Stack spacing={1} sx={{ ml: 2, mb: 2 }}>
                {businessImpactAnalysis.root_cause_analysis.map(
                  (cause, index) => (
                    <Typography
                      key={index}
                      sx={{ fontSize: "16px", fontWeight: 400 }}
                      color="text.secondary"
                    >
                      {index + 1}. {cause}
                    </Typography>
                  )
                )}
              </Stack>
            ) : (
              <Typography
                sx={{ fontSize: "16px", fontWeight: 400, mb: 2 }}
                color="text.secondary"
              >
                No root cause analysis available
              </Typography>
            )}
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              mt: 4,
              bgcolor: "grey.50",
              border: "1px solid",
              borderColor: "#787878",
              borderRadius: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: 400,
                mb: 1,
                color: "#E3112F",
              }}
            >
              <strong>Action Items:</strong>
            </Typography>
            {businessImpactAnalysis?.tangible_actions?.length > 0 ? (
              <Stack spacing={1} sx={{ ml: 2 }}>
                {businessImpactAnalysis.tangible_actions.map(
                  (actionItem, index) => (
                    <Stack
                      key={index}
                      direction="row"
                      spacing={1}
                      alignItems="flex-start"
                    >
                      <Typography
                        sx={{ fontSize: "16px", fontWeight: 400 }}
                        color="text.secondary"
                      >
                        {index + 1}. {actionItem.action}
                      </Typography>
                      {/* <Chip
                        label={actionItem.responsible_role}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ ml: 1 }}
                      /> */}
                    </Stack>
                  )
                )}
              </Stack>
            ) : (
              <Typography
                sx={{ fontSize: "16px", fontWeight: 400 }}
                color="text.secondary"
              >
                No Action Items available
              </Typography>
            )}
          </Paper>
        </Paper>

        {/* Technical Impact  Rate */}
        <Paper
          elevation={1}
          sx={{
            p: 3,
            border: "1px solid",
            borderColor: "#5391F7",
            borderRadius: 2,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            mb={3}
            sx={{ position: "relative" }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <FlashOnIcon
                sx={{
                  color: "#9333ea", // purple-600 equivalent
                  fontSize: "1.5rem", // h-5 w-5 equivalent (20px)
                  marginRight: "0.5rem", // mr-2 equivalent
                }}
              />
              <Typography sx={{ fontSize: "24px", fontWeight: 400 }}>
                Technical Impact Overview
              </Typography>
            </Stack>

            <Tooltip
              title={tooltipTexts["Technical Impact Score"]}
              placement="top"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: 300,
                    fontSize: "14px",
                    lineHeight: 1.4,
                  },
                },
              }}
            >
              <IconButton
                size="small"
                sx={{
                  padding: "4px",
                  color: "#5391F7",
                  "&:hover": {
                    backgroundColor: "rgba(83, 145, 247, 0.1)",
                  },
                }}
                aria-label="Info"
              >
                <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
              </IconButton>
            </Tooltip>
          </Stack>

          <Grid
            item
            xs={12}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <GaugeChartMUI
              value={securityMetrics.technicalImpact}
              max={10}
              color="#ea580c"
              size={150}
            />
            <Typography sx={{ fontSize: "18px", fontWeight: 500 }}>
              {getTechnicalImpactDescription(
                singleRunSecurityMetrics.technicalImpact
              )}
            </Typography>
            <Typography sx={{ fontSize: "18px", fontWeight: 500, mt: 1 }}>
              Technical Impact Score
            </Typography>
          </Grid>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              mt: 4,
              bgcolor: "grey.50",
              border: "1px solid",
              borderColor: "#787878",
              borderRadius: 1,
            }}
          >
            <Typography sx={{ fontSize: "18px", fontWeight: 400, mb: 1 }}>
              <strong>Insights:</strong>
            </Typography>
            {technicalImpactAnalysis?.key_insights?.length > 0 ? (
              <Stack spacing={1} sx={{ ml: 2, mb: 2 }}>
                {technicalImpactAnalysis.key_insights.map((cause, index) => (
                  <Typography
                    key={index}
                    sx={{ fontSize: "16px", fontWeight: 400 }}
                    color="text.secondary"
                  >
                    {index + 1}. {cause}
                  </Typography>
                ))}
              </Stack>
            ) : (
              <Typography
                sx={{ fontSize: "16px", fontWeight: 400, mb: 2 }}
                color="text.secondary"
              >
                No insights available
              </Typography>
            )}

            <Typography sx={{ fontSize: "18px", fontWeight: 400, mb: 1 }}>
              <strong>Root Cause:</strong>
            </Typography>
            {technicalImpactAnalysis?.root_cause_analysis?.length > 0 ? (
              <Stack spacing={1} sx={{ ml: 2, mb: 2 }}>
                {technicalImpactAnalysis.root_cause_analysis.map(
                  (cause, index) => (
                    <Typography
                      key={index}
                      sx={{ fontSize: "16px", fontWeight: 400 }}
                      color="text.secondary"
                    >
                      {index + 1}. {cause}
                    </Typography>
                  )
                )}
              </Stack>
            ) : (
              <Typography
                sx={{ fontSize: "16px", fontWeight: 400, mb: 2 }}
                color="text.secondary"
              >
                No root cause analysis available
              </Typography>
            )}
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              mt: 4,
              bgcolor: "grey.50",
              border: "1px solid",
              borderColor: "#787878",
              borderRadius: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: 400,
                mb: 1,
                color: "#E3112F",
              }}
            >
              <strong>Action Items:</strong>
            </Typography>
            {technicalImpactAnalysis?.tangible_actions?.length > 0 ? (
              <Stack spacing={1} sx={{ ml: 2 }}>
                {technicalImpactAnalysis.tangible_actions.map(
                  (actionItem, index) => (
                    <Stack
                      key={index}
                      direction="row"
                      spacing={1}
                      alignItems="flex-start"
                    >
                      <Typography
                        sx={{ fontSize: "16px", fontWeight: 400 }}
                        color="text.secondary"
                      >
                        {index + 1}. {actionItem.action}
                      </Typography>
                      {/* <Chip
                        label={actionItem.responsible_role}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ ml: 1 }}
                      /> */}
                    </Stack>
                  )
                )}
              </Stack>
            ) : (
              <Typography
                sx={{ fontSize: "16px", fontWeight: 400 }}
                color="text.secondary"
              >
                No Action Items available
              </Typography>
            )}
          </Paper>
        </Paper>

        {/* Strategic Recommendations Summary */}
        <Paper
          elevation={1}
          sx={{
            p: 3,
            // background: "linear-gradient(to right, #f9fafb, #f3f4f6)",
            border: "1px solid",
            borderColor: "#5391F7",
            borderRadius: 2,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            mb={3}
            sx={{ position: "relative" }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Target sx={{ color: (theme) => theme.palette.primary.main }} />
              <Typography sx={{ fontSize: "24px", fontWeight: 400 }}>
                Strategic Recommendations Summary
              </Typography>
            </Stack>

            <Tooltip
              title={tooltipTexts["Strategic Recommendations Summary"]}
              placement="top"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: 300,
                    fontSize: "14px",
                    lineHeight: 1.4,
                  },
                },
              }}
            >
              <IconButton
                size="small"
                sx={{
                  padding: "4px",
                  color: "#5391F7",
                  "&:hover": {
                    backgroundColor: "rgba(83, 145, 247, 0.1)",
                  },
                }}
                aria-label="Info"
              >
                <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
              </IconButton>
            </Tooltip>
          </Stack>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography sx={typographyStyles.subtitle} gutterBottom>
                Immediate Actions
              </Typography>
              <List>
                {StrategicRecsAnalysis?.immediate_actions?.map(
                  (action, index) => (
                    <ListItem key={`immediate-${index}`}>
                      <ListItemText
                        primary={action.action}
                        secondary={`Responsible: ${action.responsible_role}`}
                        primaryTypographyProps={{
                          sx: { fontSize: "16px", fontWeight: 400 },
                        }}
                        secondaryTypographyProps={{
                          sx: {
                            fontSize: "14px",
                            color: "text.secondary",
                            mt: 0.5,
                          },
                        }}
                      />
                    </ListItem>
                  )
                )}
              </List>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography sx={typographyStyles.subtitle} gutterBottom>
                Long-term Strategy
              </Typography>
              <List>
                {StrategicRecsAnalysis?.long_term_strategy?.map(
                  (strategy, index) => (
                    <ListItem key={`longterm-${index}`}>
                      {/* <ListItemIcon>
                                {index === 0 && <TrendingUp color="success" />}
                                {index === 1 && <CheckCircle color="secondary" />}
                                {index === 2 && <Build color="warning" />}
                                {index > 2 && <Engineering color="primary" />}
                              </ListItemIcon> */}
                      <ListItemText
                        primary={strategy.action}
                        secondary={`Responsible: ${strategy.responsible_role}`}
                        primaryTypographyProps={{
                          sx: { fontSize: "16px", fontWeight: 400 },
                        }}
                        secondaryTypographyProps={{
                          sx: {
                            fontSize: "14px",
                            color: "text.secondary",
                            mt: 0.5,
                          },
                        }}
                      />
                    </ListItem>
                  )
                ) || (
                  // Fallback to static content if no data
                  <>
                    <ListItem>
                      <ListItemIcon>
                        <TrendingUp color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Integrate DAST into CI/CD pipeline"
                        primaryTypographyProps={{
                          sx: { fontSize: "16px", fontWeight: 400 },
                        }}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle color="secondary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Expand test coverage with SAST/IAST"
                        primaryTypographyProps={{
                          sx: { fontSize: "16px", fontWeight: 400 },
                        }}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Build color="warning" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Establish automated remediation workflows"
                        primaryTypographyProps={{
                          sx: { fontSize: "16px", fontWeight: 400 },
                        }}
                      />
                    </ListItem>
                  </>
                )}
              </List>
            </Grid>
          </Grid>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              mt: 4,
              bgcolor: "grey.50",
              border: "1px solid",
              borderColor: "#787878",
              borderRadius: 1,
            }}
          >
            <Typography sx={{ fontSize: "18px", fontWeight: 400, mb: 1 }}>
              <strong>Insights:</strong>
            </Typography>
            {StrategicRecsAnalysis?.key_insights?.length > 0 ? (
              <Stack spacing={1} sx={{ ml: 2, mb: 2 }}>
                {StrategicRecsAnalysis.key_insights.map((cause, index) => (
                  <Typography
                    key={index}
                    sx={{ fontSize: "16px", fontWeight: 400 }}
                    color="text.secondary"
                  >
                    {index + 1}. {cause}
                  </Typography>
                ))}
              </Stack>
            ) : (
              <Typography
                sx={{ fontSize: "16px", fontWeight: 400, mb: 2 }}
                color="text.secondary"
              >
                No insights available
              </Typography>
            )}

            <Typography sx={{ fontSize: "18px", fontWeight: 400, mb: 1 }}>
              <strong>Root Cause:</strong>
            </Typography>
            {StrategicRecsAnalysis?.root_cause_analysis?.length > 0 ? (
              <Stack spacing={1} sx={{ ml: 2, mb: 2 }}>
                {StrategicRecsAnalysis.root_cause_analysis.map(
                  (cause, index) => (
                    <Typography
                      key={index}
                      sx={{ fontSize: "16px", fontWeight: 400 }}
                      color="text.secondary"
                    >
                      {index + 1}. {cause}
                    </Typography>
                  )
                )}
              </Stack>
            ) : (
              <Typography
                sx={{ fontSize: "16px", fontWeight: 400, mb: 2 }}
                color="text.secondary"
              >
                No root cause analysis available
              </Typography>
            )}
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              mt: 4,
              bgcolor: "grey.50",
              border: "1px solid",
              borderColor: "#787878",
              borderRadius: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: 400,
                mb: 1,
                color: "#E3112F",
              }}
            >
              <strong>Action Items:</strong>
            </Typography>
            {StrategicRecsAnalysis?.tangible_actions?.length > 0 ? (
              <Stack spacing={1} sx={{ ml: 2 }}>
                {StrategicRecsAnalysis.tangible_actions.map(
                  (actionItem, index) => (
                    <Stack
                      key={index}
                      direction="row"
                      spacing={1}
                      alignItems="flex-start"
                    >
                      <Typography sx={{ fontSize: "16px", fontWeight: 600 }}>
                        {actionItem.responsible_role}:
                      </Typography>
                      <Typography
                        sx={{ fontSize: "16px", fontWeight: 400 }}
                        color="text.secondary"
                      >
                        {actionItem.action}
                      </Typography>
                    </Stack>
                  )
                )}
              </Stack>
            ) : (
              <Typography
                sx={{ fontSize: "16px", fontWeight: 400 }}
                color="text.secondary"
              >
                No Action Items available
              </Typography>
            )}
          </Paper>
        </Paper>
      </Stack>
    </Box>
  );
}
